/** Every user-facing string for contact enrichment, renamable in one place.
 *  "Contact enrichment", never bare "enrichment" — that word already means
 *  AI capture processing elsewhere in this codebase. */
export const ENRICHMENT_COPY = {
  title: 'Contact enrichment',
  enrichButton: 'Enrich',
  // The Enrich button's own `loading` label: the agent-backed request can
  // run tens of seconds, so the plain "Enrich" text (which just sits next to
  // a spinner) is replaced with this while it's in flight.
  researching: 'Researching the web…',
  inProgress: 'Working on it — checking your captures, searching the web and looking up contacts. This can take a minute or so.',
  useValue: 'Use this value',
  reject: 'Reject',
  alreadyMatches: 'Already matches the captured value',
  hypothesis: 'Pattern guess — unverified',
  // Confidence tier words (Round 2, provenance polish 2026-09-23): the raw
  // percentage is no longer shown as visible text — it moved into the
  // tooltip on hover instead, via confidenceTooltip below.
  tierStrong: 'Strong',
  tierGood: 'Good',
  tierWeak: 'Weak',
  confidenceTooltip: (pct: number) => `${pct}% confidence`,
  // Round 2 (human-readable source tags, 2026-09-23): the ONLY source chip a
  // candidate row shows — keyed by `candidate.source`. The raw source key
  // (`capture`, `prior_capture`, ...) is never rendered; an unmapped source
  // falls back to a humanized version of the raw key instead (see
  // SOURCE_LABEL / humanizeSourceKey in ContactEnrichmentPanel.tsx).
  // sourcePriorCapture renamed from "Previous capture" to "Company capture"
  // per the user — a repeat visitor's contact info came from elsewhere in
  // THIS company, not necessarily from this same person before.
  sourceAgent: 'Web research',
  sourceCapture: 'Your capture data',
  sourcePriorCapture: 'Company capture',
  sourceApollo: 'Apollo match',
  sourceCompanySite: 'Company main line',
  sourceAgentUnverified: 'Web research — unverified',
  needsReview: 'Needs review — could not verify',
  invalidWarning: 'Verifier says invalid',
  catchAllWarning: 'Catch-all domain — cannot confirm mailbox',
  rejectedGroup: 'Rejected',
  foundNothing: 'Looked — nothing found',
  stillLooking: 'Still looking…',
  // Shown under stillLooking specifically: Apollo's waterfall mode answers
  // email requests synchronously but resolves phone via an async callback
  // that can take a while — this says it's safe to navigate away.
  stillLookingHint: 'Phone results can take a while — safe to close, they arrive on their own',
  couldNotLook: 'Could not look',
  retryApollo: 'Retry Apollo',
  // Distinct from couldNotLook: the daily spend ceiling isn't a failure, it's
  // an expected stop condition — same notice whether it arrives as a 429 or
  // as a successful response that says Apollo was skipped for the day.
  outOfCredits: 'Daily Apollo budget reached — try tomorrow or raise the ceiling',
  // Go-live gates (2026-10-06): the backend now reports WHY Apollo was
  // skipped. These are informational — the free levels still ran.
  // "What ran" strip (2026-10-10): after an Enrich click, one line per layer
  // of the ladder so a layer that ran and found nothing never looks like
  // "nothing happened".
  levelsTitle: 'What ran',
  layerCapture: 'Internal data',
  layerAgent: 'AI web research',
  layerApollo: 'Apollo',
  levelNotOn: 'Not turned on',
  levelVerbCapture: 'Checked your captures',
  levelVerbAgent: 'Searched the web',
  levelVerbApollo: 'Looked up',
  levelNothingFound: 'nothing found',
  levelFoundPrefix: 'found',
  levelDiscarded: (n: number) => `${n} ${n === 1 ? 'answer' : 'answers'} discarded (could not be verified)`,
  levelRanOutOfTime: 'ran out of time before finishing',
  levelSeconds: (ms: number) => `${Math.max(1, Math.round(ms / 1000))} s`,
  levelSkipped: {
    found_earlier: 'Not needed — an earlier step already found it',
    nothing_missing: 'Not needed — nothing is missing on this lead',
    not_requested: 'Not part of this check',
    no_domain: 'Skipped — no company web domain is known for this lead',
    phone_only: "Skipped — only a phone is missing, and this lookup can't return phones",
    confirm_required: 'Skipped — the spend was not confirmed',
    daily_ceiling: 'Skipped — daily limit reached',
    phone_pending: 'Not needed again — a phone request is already on its way',
    not_reached: 'Not reached — an earlier step failed',
  } as Record<string, string>,
  levelSkippedFallback: 'Skipped',
  levelFailed: 'Could not run',
  // Why a layer could not run, when the backend says. Keyed by cause; 'error' has no extra words.
  levelFailedCause: {
    rate_limited: 'the service was busy, so click Enrich to try again in a minute',
    unavailable: "the service didn't respond, so click Enrich to try again",
    timeout: 'it took too long, so click Enrich to try again',
  } as Record<string, string>,
  levelNothingYet: 'nothing yet',
  levelAwaiting: (fields: string) => `${fields} requested — it can take a few minutes to arrive`,
  levelNeedsReview: (n: number) => `${n} ${n === 1 ? 'answer' : 'answers'} could not be verified — listed below for you to review`,
  companyPhoneTitle: 'Company site phone',
  companyPhoneFound: (n: number) => `found ${n} ${n === 1 ? 'number' : 'numbers'} the company shows on its own site`,
  companyPhoneNone: 'no number found on its own site',
  companyPhoneChecked: 'checked',
  levelFailedThin: 'Not enough to go on — a last name and a company or web domain are needed',
  // Persisted run history (migration 053): what the page shows after a reload.
  lastRunPrefix: 'Last run',
  // Shown instead of a raw Entra object id (runs recorded before the backend
  // learned to read the actor's login from the token).
  unknownActor: 'a VendoIQ super-admin',
  lastRunBy: (when: string, by: string) => `${when} by ${by}`,
  lastRunTook: (ms: number) => `took ${Math.max(1, Math.round(ms / 1000))} s`,
  justNow: 'just now',
  wantedLine: (fields: string) => `Looking for: ${fields}`,
  foundLine: (found: string) => `Found: ${found}`,
  foundNone: 'Found: nothing',
  runOutcomeFailed: 'failed',
  runOutcomeRefused: 'not enough to go on',
  previousRuns: (n: number) => `Previous runs (${n})`,
  explicitTag: 'scoped check',
  pagesRead: (hosts: string) => `read ${hosts}`,
  pagesReadLabel: 'read',
  // Employer-domain evidence in the "What ran" strip.
  companySite: 'Company site',
  companySiteUnconfirmed: 'Company site (unconfirmed — worth a look)',
  domainKnown: "from this lead's own details",
  domainFromAgent: 'found by the web research',
  domainCheckedOn: 'checked on',
  domainNotFound: "couldn't confirm which site is this employer's",
  weighedTitle: (n: number) => `Sites weighed (${n})`,
  weighedUsed: 'used',
  weighedPassed: 'passed over',
  mailSeenOn: (domain: string) => `Company addresses at ${domain} are published on`,
  searchesMade: (n: number) => `${n} ${n === 1 ? 'search' : 'searches'}`,
  apolloNoDomain: 'Apollo was skipped — no employer web domain is known for this lead, and Apollo cannot match on a company name alone',
  apolloPhoneOnly: 'Apollo was skipped — only a phone is missing, and the synchronous lookup cannot return phones',
} as const;
